import { useState } from 'react';
import { AbstractForm, AbstractFormState, IStateForm } from '../../../../common/AbstractForm';
import FormModel from '../../../../../utils/FormModel';
import { isIpAddressInputValid } from '../../../../common/form-controls/IpAddressValidator';

export interface ISubnetStateForm extends IStateForm {
	aZ1_CIDR: string;
	aZ2_CIDR: string;
	aZ3_CIDR: string;
	name: string;
	uniqueRouteTablePerAz: boolean;
}

export interface RulesFormProps {
	isReadOnly?: boolean;
}

export interface AbstractSubnetFormState<T> extends AbstractFormState<T> {}

export const useAbstractSubnetForm = () => {
	const [state, setState] = useState<AbstractSubnetFormState<ISubnetStateForm>>({
		stateForm: {
			aZ1_CIDR: '',
			aZ2_CIDR: null,
			aZ3_CIDR: null,
			name: '',
			uniqueRouteTablePerAz: null,
		},
	});

	const abstractFormInstance = new (class extends AbstractForm<
		RulesFormProps,
		AbstractSubnetFormState<ISubnetStateForm>
	> {
		constructor() {
			super({});
			this.state = state;
		}

		getDefaultForm() {
			return {
				aZ1_CIDR: '',
				aZ2_CIDR: null,
				aZ3_CIDR: null,
				name: '',
				uniqueRouteTablePerAz: false,
			};
		}
	})();

	const [formModel] = useState<FormModel>(
		(abstractFormInstance.formModel = new FormModel({
			aZ1_CIDR: {
				getValue: () => state.stateForm.aZ1_CIDR,
				validation: {
					required: () => true,
					rules: [
						{
							assert: () => isIpAddressInputValid(state.stateForm.aZ1_CIDR, false, true),
							message: 'Not a valid IP address.',
						},
					],
				},
			},
			aZ2_CIDR: {
				getValue: () => state.stateForm.aZ2_CIDR,
				validation: {
					required: () => false,
					rules: [
						{
							assert: () => isIpAddressInputValid(state.stateForm.aZ2_CIDR, false, true),
							message: 'Not a valid IP address.',
						},
					],
				},
			},
			aZ3_CIDR: {
				getValue: () => state.stateForm.aZ3_CIDR,
				validation: {
					required: () => false,
					rules: [
						{
							assert: () => isIpAddressInputValid(state.stateForm.aZ3_CIDR, false, true),
							message: 'Not a valid IP address.',
						},
					],
				},
			},
			name: {
				getValue: () => state.stateForm.name,
				validation: {
					required: () => true,
				},
			},
			uniqueRouteTablePerAz: {
				getValue: () => state.stateForm.uniqueRouteTablePerAz,
				validation: {
					required: () => true,
				},
			},
		}))
	);

	const handleStateFormChange = (field: any, value: any) => {
		abstractFormInstance.setState = (newState) => setState((prevState) => ({ ...prevState, ...newState }));
		abstractFormInstance.handleStateFormChange(field, value);
	};

	const getStateFormHandlers = () => {
		return {
			aZ1_CIDR: {
				value: state.stateForm.aZ1_CIDR,
				validation: formModel.fields.aZ1_CIDR.validation,
				onChange: (e) => {
					handleStateFormChange('aZ1_CIDR', e.target.value);
				},
			},
			aZ2_CIDR: {
				value: state.stateForm.aZ2_CIDR,
				validation: formModel.fields.aZ2_CIDR.validation,
				onChange: (e) => {
					handleStateFormChange('aZ2_CIDR', e.target.value);
				},
			},
			aZ3_CIDR: {
				value: state.stateForm.aZ3_CIDR,
				validation: formModel.fields.aZ3_CIDR.validation,
				onChange: (e) => {
					handleStateFormChange('aZ3_CIDR', e.target.value);
				},
			},
			name: {
				value: state.stateForm.name,
				validation: formModel.fields.name.validation,
				onChange: (e) => {
					handleStateFormChange('name', e.target.value);
				},
			},
			uniqueRouteTablePerAz: {
				value: state.stateForm.uniqueRouteTablePerAz,
				validation: formModel.fields.uniqueRouteTablePerAz.validation,
				onChange: (e) => {
					handleStateFormChange('uniqueRouteTablePerAz', e.target.value);
				},
			},
		};
	};

	const getDefaultForm = () => {
		return {
			aZ1_CIDR: '',
			aZ2_CIDR: null,
			aZ3_CIDR: null,
			name: '',
			uniqueRouteTablePerAz: null,
		};
	};

	const isValid = () => {
		return formModel.isValid();
	};

	return {
		subnetStateForm: state,
		formSubnetModel: formModel,
		handleStateFormChange,
		getSubnetStateFormHandlers: getStateFormHandlers,
		isValidSubnet: isValid,
		getDefaultForm,
	};
};
